<script setup lang="ts">
import { definePage } from "unplugin-vue-router/runtime";
import { computed, ref } from "vue";
import { useAbility } from "@/api/models/auth/ability";
import { useMsoTransactionLegsQuery } from "@/composables/models/practice/useMsoBilling";
import {
  MSO_INVOICE_STATUS_COLORS,
  useMsoInvoiceDetail,
} from "@/composables/models/billing/useMsoInvoiceDetail";
import TransactionHistoryCard from "@/components/models/billing/TransactionHistoryCard.vue";
import InvoiceDetailDialog from "@/components/models/billing/InvoiceDetailDialog.vue";
import PayoutDetailDialog from "@/components/models/billing/PayoutDetailDialog.vue";
import BillingSection from "@/components/models/practice/detail/BillingSection.vue";
import BillingShell from "@/components/models/billing/BillingShell.vue";
import { canOpenBillingPage, readBillingRights } from "@/composables/shared/billingAccess";
import { useBillingOwner } from "@/composables/shared/useBillingOwner";

definePage({
  meta: {
    title: "generic.fields.sections.transactionHistory",
    checkPrivileges: () => {
      const { can } = useAbility();
      return canOpenBillingPage(readBillingRights(can), "transactions");
    },
  },
});

const { scope, activeMso } = useBillingOwner();
const msoId = computed(() => (scope.value === "mso" ? activeMso.value?.id : undefined));
const { data, isLoading } = useMsoTransactionLegsQuery(
  computed(() => ({ msoId: msoId.value ?? "" })),
  computed(() => Boolean(msoId.value))
);
const legs = computed(() => data.value?.legs ?? []);

const { billedTo, billedToAddress, downloadingId, downloadPdf } = useMsoInvoiceDetail(msoId);
const detailInvoiceId = ref<string | null>(null);
const detailPayoutId = ref<string | null>(null);
</script>

<template>
  <billing-shell page="transactions">
    <template #default="{ scope: ownerScope, company }">
      <template v-if="ownerScope === 'mso' && msoId">
        <transaction-history-card
          :key="`mso-${msoId}`"
          :legs="legs"
          :is-loading="isLoading"
          @open-invoice="detailInvoiceId = $event"
          @open-payout="detailPayoutId = $event"
        />

        <payout-detail-dialog
          scope="mso"
          :owner-id="msoId"
          :payout-id="detailPayoutId"
          @close="detailPayoutId = null"
        />

        <invoice-detail-dialog
          :mso-id="msoId"
          :invoice-id="detailInvoiceId"
          :billed-to="billedTo"
          :billed-to-address="billedToAddress"
          :status-colors="MSO_INVOICE_STATUS_COLORS"
          :is-downloading="downloadingId === detailInvoiceId"
          @close="detailInvoiceId = null"
          @download="downloadPdf"
        />
      </template>

      <billing-section
        v-else-if="ownerScope === 'company' && company"
        :key="`company-${company.id}`"
        :company-id="company.id"
        tab="transactions"
      />
    </template>
  </billing-shell>
</template>
